import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { useMusic24Capabilities } from '../../music24/hooks/useMusic24Capabilities';
import { PresaveCounterCard } from './PresaveCounterCard';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole panel; each part uses its own slice of it */
import styles from './PresavePanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PresaveSubscribersCardProps {
  /** The artist subscribers who get the next release (`artistSubscriberCount` of the stats). */
  count: number;
  /** The song of the campaign. */
  title: string;
}

/**
 * The third counter of a scheduled campaign (design presave-subscriptions d1): the fans who
 * subscribed to the artist get this song too, with no pre-save. Nothing shows with no subscriber.
 * A workspace with no pre-saves (an old campaign on Free) sends the subscribers an email only (D5).
 */
const PresaveSubscribersCardComponent: React.FC<PresaveSubscribersCardProps> = ({ count, title }) => {
  const intl = useIntl();
  const { canUsePresave } = useMusic24Capabilities();

  if (count <= 0) {
    return null;
  }

  return (
    <PresaveCounterCard
      className={styles.cardHighlight}
      testId="presave-subscribers-card"
      label={intl.formatMessage({ id: 'app.sml.presave.panel.subscribers', defaultMessage: 'Your subscribers' })}
      value={intl.formatNumber(count)}
      unit={
        canUsePresave
          ? intl.formatMessage({ id: 'app.sml.presave.panel.subscribers.unit', defaultMessage: 'will get this' })
          : intl.formatMessage({
              id: 'app.sml.presave.panel.subscribers.unitEmail',
              defaultMessage: 'will get an email',
            })
      }
    >
      <p className={styles.cardNote}>
        {canUsePresave
          ? intl.formatMessage(
              {
                id: 'app.sml.presave.panel.subscribers.note',
                defaultMessage: '{title} goes into their library on release day, with no pre-save.',
              },
              { title }
            )
          : intl.formatMessage({
              id: 'app.sml.presave.panel.subscribers.noteEmail',
              defaultMessage: 'On Free, subscribers get the email only.',
            })}
      </p>
    </PresaveCounterCard>
  );
};

export const PresaveSubscribersCard = TypedMemo(PresaveSubscribersCardComponent);
